import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Search,
  Filter,
  Trash2,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Key,
  AlertTriangle,
  RefreshCw,
  Copy,
  Edit,
  Layers,
  Loader2,
  Database,
  CheckCircle,
  Users,
  Settings,
  Clock
} from 'lucide-react'
import { licenseApi, avatarApi, dialerApi, linksApi, API_BASE_URL } from '../services/api'
import { useAuth } from '../context/AuthContext'
import './Licenses.css'

const PAGE_SIZES = [50, 100, 200, 500]

// ============================================
// ENCRYPTION UTILITIES - Match backend common.js EXACTLY
// ============================================

/**
 * Generate random token - matches common.js generateToken()
 * Uses lowercase letters + digits, 16 characters
 */
function generateToken() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let token = ''
  for (let i = 0; i < 16; i++) {
    // Using Math.round like Angular/backend does
    token += chars.charAt(Math.round(Math.random() * (chars.length - 1)))
  }
  return token
}

/**
 * Encrypt text with salt - matches common.js crypt() EXACTLY
 * This is copied directly from your backend common.js
 */
function crypt(text, salt) {
  const textToChars = (text) => text.split("").map((c) => c.charCodeAt(0))
  const byteHex = (n) => ("0" + Number(n).toString(16)).substr(-2)
  const applySaltToChar = (code) => textToChars(salt).reduce((a, b) => a ^ b, code)

  return text
    .split("")
    .map(textToChars)
    .map(applySaltToChar)
    .map(byteHex)
    .join("")
}

// The encryption key - MUST match backend .env ENCRYPTION_KEY
const ENCRYPTION_KEY = 'theFutureIsBright'

/**
 * Generate key_string from token
 * This is what the bot uses to authenticate
 */
function generateKeyString(token) {
  return crypt(token, ENCRYPTION_KEY)
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.03 } }
}

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } }
}

function Licenses() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  // Data states
  const [licenses, setLicenses] = useState([])
  const [avatars, setAvatars] = useState([])
  const [dialers, setDialers] = useState([])
  const [accessLinks, setAccessLinks] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [idMin, setIdMin] = useState('')
  const [idMax, setIdMax] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filterAll, setFilterAll] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)

  // Selection states
  const [selectedLicenses, setSelectedLicenses] = useState([])

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [currentLicense, setCurrentLicense] = useState(null)
  const [saving, setSaving] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Form data - matches Angular schema EXACTLY
  const [formData, setFormData] = useState({
    dialer_id: '',
    access_link: '',
    users: '',
    passwords: '',
    avatar_id: '',
    campaign: '',
    // Bot configuration
    login_method: 'double-merged',
    transfer_method: 'd1',
    xfer_disposition_selector: 'call-transfered',
    transcriber_type: 'VOSK',
    dont_wait: true,
    comment_login_submit_btn: true,
    comment_2nd_hello_logic: true,
    cmnt_40sec_call_limit_check: false,
    dispo_in_cb_after_40sec: false,
    debug_mode: false,
    number_of_audios: 1
  })

  // Show advanced config toggle
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Bulk edit field selectors (matches Angular checkboxes)
  const [fieldSelectors, setFieldSelectors] = useState({
    dialer: false,
    access_link: false,
    user: false,
    password: false,
    avatar: false,
    campaign: false,
    // Bot configuration selectors
    login_method: false,
    transfer_method: false,
    xfer_disposition_selector: false,
    transcriber_type: false,
    dont_wait: false,
    comment_login_submit_btn: false,
    comment_2nd_hello_logic: false,
    cmnt_40sec_call_limit_check: false,
    dispo_in_cb_after_40sec: false,
    debug_mode: false,
    number_of_audios: false
  })

  // Validation alerts
  const [usersAlert, setUsersAlert] = useState('')
  const [passwordsAlert, setPasswordsAlert] = useState('')
  const [hasChanged, setHasChanged] = useState(false)

  // Check access
  useEffect(() => {
    if (currentUser && currentUser.access_to_licenses !== true && currentUser.access_to_licenses !== 1) {
      navigate('/')
      return
    }
    loadData()
  }, [currentUser, navigate])

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    try {
      const [licensesRes, avatarsRes, dialersRes, linksRes] = await Promise.allSettled([
        licenseApi.getAllWithAvatars(),
        avatarApi.getAll(),
        dialerApi.getAll(),
        linksApi.getAll()
      ])

      const licensesData = licensesRes.status === 'fulfilled' ? licensesRes.value.data : []
      const avatarsData = avatarsRes.status === 'fulfilled' ? avatarsRes.value.data : []
      const dialersData = dialersRes.status === 'fulfilled' ? dialersRes.value.data : []
      const linksData = linksRes.status === 'fulfilled' ? linksRes.value.data : []

      setLicenses(Array.isArray(licensesData) ? licensesData : [])
      setAvatars(Array.isArray(avatarsData) ? avatarsData : [])
      setDialers(Array.isArray(dialersData) ? dialersData : [])

      // Parse access_links from links table (matches Angular)
      if (linksData.length > 0 && linksData[0].links) {
        try {
          const parsedLinks = JSON.parse(linksData[0].links)
          setAccessLinks(Array.isArray(parsedLinks) ? parsedLinks : [])
        } catch (e) {
          setAccessLinks([])
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Create lookup maps
  const avatarMap = useMemo(() => {
    const map = new Map()
    avatars.forEach(a => map.set(a.id, a.name))
    return map
  }, [avatars])

  const dialerMap = useMemo(() => {
    const map = new Map()
    dialers.forEach(d => map.set(d.id, d.name))
    return map
  }, [dialers])

  // Stats calculation
  const stats = useMemo(() => {
    const total = licenses.length
    const uniqueDialers = new Set(licenses.map(l => l.dialer_id)).size
    const uniqueAvatars = new Set(licenses.map(l => l.avatar_id)).size
    const withKeys = licenses.filter(l => l.key_string).length
    return { total, uniqueDialers, uniqueAvatars, withKeys }
  }, [licenses])

  // Filter licenses (matches Angular filter())
  const filteredLicenses = useMemo(() => {
    const queryLower = searchQuery.toLowerCase()
    const minId = idMin ? parseInt(idMin) : null
    const maxId = idMax ? parseInt(idMax) : null

    if (!searchQuery && !idMin && !idMax) {
      return licenses
    }

    return licenses.filter(license => {
      // Filter by dialer name (matches Angular)
      if (searchQuery) {
        const dialerName = (license.dialer || '').toLowerCase()
        if (!dialerName.includes(queryLower)) return false
      }

      // ID range filter
      if (minId !== null && license.id < minId) return false
      if (maxId !== null && license.id > maxId) return false

      return true
    })
  }, [licenses, searchQuery, idMin, idMax])

  // Pagination
  const totalPages = Math.ceil(filteredLicenses.length / pageSize) || 1
  const paginatedLicenses = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredLicenses.slice(start, start + pageSize)
  }, [filteredLicenses, currentPage, pageSize])

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, idMin, idMax, pageSize])

  // Selection handlers (matches Angular evalCheckboxChange)
  const handleSelect = (id) => {
    setSelectedLicenses(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  // Select all filtered (matches Angular evalAll)
  const handleSelectAllFiltered = (checked) => {
    setFilterAll(checked)
    if (checked) {
      setSelectedLicenses(filteredLicenses.map(l => l.id))
    } else {
      setSelectedLicenses([])
    }
  }

  const clearSelection = () => {
    setSelectedLicenses([])
    setFilterAll(false)
  }

  const clearFilters = () => {
    setSearchQuery('')
    setIdMin('')
    setIdMax('')
    setFilterAll(false)
    setSelectedLicenses([])
  }

  // Clear field selectors (matches Angular clearFieldSelectors)
  const clearFieldSelectors = () => {
    setFieldSelectors({
      dialer: false,
      access_link: false,
      user: false,
      password: false,
      avatar: false,
      campaign: false,
      // Bot configuration selectors
      login_method: false,
      transfer_method: false,
      xfer_disposition_selector: false,
      transcriber_type: false,
      dont_wait: false,
      comment_login_submit_btn: false,
      comment_2nd_hello_logic: false,
      cmnt_40sec_call_limit_check: false,
      dispo_in_cb_after_40sec: false,
      debug_mode: false,
      number_of_audios: false
    })
  }

  // Modal handlers
  const openAddModal = () => {
    setModalMode('add')
    setCurrentLicense(null)
    clearFieldSelectors()
    setUsersAlert('')
    setPasswordsAlert('')
    setHasChanged(false)
    setShowAdvanced(false)
    setFormData({
      dialer_id: dialers[0]?.id?.toString() || '',
      access_link: accessLinks[0] || '',
      users: '',
      passwords: '',
      avatar_id: avatars[0]?.id?.toString() || '',
      campaign: '',
      // Bot configuration defaults
      login_method: 'double-merged',
      transfer_method: 'd1',
      xfer_disposition_selector: 'call-transfered',
      transcriber_type: 'VOSK',
      dont_wait: true,
      comment_login_submit_btn: true,
      comment_2nd_hello_logic: true,
      cmnt_40sec_call_limit_check: false,
      dispo_in_cb_after_40sec: false,
      debug_mode: false,
      number_of_audios: 1
    })
    setShowModal(true)
  }

  // Edit single item (matches Angular editItem)
  const openEditModal = (license) => {
    setModalMode('edit')
    setCurrentLicense(license)
    clearFieldSelectors()
    setUsersAlert('')
    setPasswordsAlert('')
    setHasChanged(false)
    setShowAdvanced(false)
    setFormData({
      dialer_id: license.dialer_id?.toString() || '',
      access_link: license.access_link || '',
      users: license.user || '',
      passwords: license.password || '',
      avatar_id: license.avatar_id?.toString() || '',
      campaign: license.campaign || '',
      // Bot configuration from license
      login_method: license.login_method || 'double-merged',
      transfer_method: license.transfer_method || 'd1',
      xfer_disposition_selector: license.xfer_disposition_selector || 'call-transfered',
      transcriber_type: license.transcriber_type || 'VOSK',
      dont_wait: license.dont_wait !== 0,
      comment_login_submit_btn: license.comment_login_submit_btn !== 0,
      comment_2nd_hello_logic: license.comment_2nd_hello_logic !== 0,
      cmnt_40sec_call_limit_check: license.cmnt_40sec_call_limit_check === 1,
      dispo_in_cb_after_40sec: license.dispo_in_cb_after_40sec === 1,
      debug_mode: license.debug_mode === 1,
      number_of_audios: license.number_of_audios || 1
    })
    setShowModal(true)
  }

  // Edit selected (matches Angular editSelected)
  const openBulkEditModal = () => {
    if (selectedLicenses.length === 0) return

    const selected = licenses.filter(l => selectedLicenses.includes(l.id))
    setModalMode('bulk-edit')
    setCurrentLicense(selected[0])
    clearFieldSelectors()
    setUsersAlert('')
    setPasswordsAlert('')
    setHasChanged(false)
    setShowAdvanced(false)
    setFormData({
      dialer_id: selected[0].dialer_id?.toString() || '',
      access_link: selected[0].access_link || '',
      users: selected.map(l => l.user).join(', '),
      passwords: selected.map(l => l.password).join(', '),
      avatar_id: selected[0].avatar_id?.toString() || '',
      campaign: selected[0].campaign || '',
      // Bot configuration from first selected license
      login_method: selected[0].login_method || 'double-merged',
      transfer_method: selected[0].transfer_method || 'd1',
      xfer_disposition_selector: selected[0].xfer_disposition_selector || 'call-transfered',
      transcriber_type: selected[0].transcriber_type || 'VOSK',
      dont_wait: selected[0].dont_wait !== 0,
      comment_login_submit_btn: selected[0].comment_login_submit_btn !== 0,
      comment_2nd_hello_logic: selected[0].comment_2nd_hello_logic !== 0,
      cmnt_40sec_call_limit_check: selected[0].cmnt_40sec_call_limit_check === 1,
      dispo_in_cb_after_40sec: selected[0].dispo_in_cb_after_40sec === 1,
      debug_mode: selected[0].debug_mode === 1,
      number_of_audios: selected[0].number_of_audios || 1
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setCurrentLicense(null)
  }

  // Validation (matches Angular evalUsers/evalPasswords)
  const validateUsers = (usersStr) => {
    const users = usersStr.split(',').map(u => u.trim()).filter(u => u)

    if (selectedLicenses.length > 0 && users.length !== selectedLicenses.length) {
      setUsersAlert(`The number of selected licenses (${selectedLicenses.length}) doesn't match number of users (${users.length})`)
    } else if (modalMode === 'edit' && selectedLicenses.length === 0 && users.length > 1) {
      setUsersAlert('To edit more than one license, select multiple licenses and use the "Edit Selected" button.')
    } else {
      setUsersAlert('')
    }
  }

  const validatePasswords = (passwordsStr) => {
    const passwords = passwordsStr.split(',').map(p => p.trim()).filter(p => p)

    if (selectedLicenses.length > 0 && passwords.length !== selectedLicenses.length) {
      setPasswordsAlert(`The number of selected licenses (${selectedLicenses.length}) doesn't match number of passwords (${passwords.length})`)
    } else if (modalMode === 'edit' && selectedLicenses.length === 0 && passwords.length > 1) {
      setPasswordsAlert('To edit more than one license, select multiple licenses and use the "Edit Selected" button.')
    } else {
      setPasswordsAlert('')
    }
  }

  // Save handler - matches Angular saveLicense() EXACTLY
  const handleSave = async () => {
    if (usersAlert || passwordsAlert) return

    setSaving(true)

    try {
      const users = Array.isArray(formData.users)
        ? formData.users
        : formData.users.split(',').map(u => u.trim()).filter(u => u)

      const passwords = Array.isArray(formData.passwords)
        ? formData.passwords
        : formData.passwords.split(',').map(p => p.trim()).filter(p => p)

      const dialerName = dialers.find(d => d.id === parseInt(formData.dialer_id))?.name || ''

      if (currentLicense?.id) {
        // EDIT MODE
        if (selectedLicenses.length > 0) {
          // Bulk edit with field selectors (matches Angular's selective update)
          const selected = licenses.filter(l => selectedLicenses.includes(l.id))

          for (let i = 0; i < selected.length; i++) {
            const license = selected[i]
            const updateData = { id: license.id }

            if (fieldSelectors.dialer) {
              updateData.dialer_id = parseInt(formData.dialer_id)
              updateData.dialer = dialerName
            }
            if (fieldSelectors.access_link) {
              updateData.access_link = formData.access_link
            }
            if (fieldSelectors.user && users[i]) {
              updateData.user = users[i]
            }
            if (fieldSelectors.password && passwords[i]) {
              updateData.password = passwords[i]
            }
            if (fieldSelectors.avatar) {
              updateData.avatar_id = parseInt(formData.avatar_id)
            }
            if (fieldSelectors.campaign) {
              updateData.campaign = formData.campaign
            }
            // Bot configuration field selectors
            if (fieldSelectors.login_method) {
              updateData.login_method = formData.login_method
            }
            if (fieldSelectors.transfer_method) {
              updateData.transfer_method = formData.transfer_method
            }
            if (fieldSelectors.xfer_disposition_selector) {
              updateData.xfer_disposition_selector = formData.xfer_disposition_selector
            }
            if (fieldSelectors.transcriber_type) {
              updateData.transcriber_type = formData.transcriber_type
            }
            if (fieldSelectors.dont_wait) {
              updateData.dont_wait = formData.dont_wait ? 1 : 0
            }
            if (fieldSelectors.comment_login_submit_btn) {
              updateData.comment_login_submit_btn = formData.comment_login_submit_btn ? 1 : 0
            }
            if (fieldSelectors.comment_2nd_hello_logic) {
              updateData.comment_2nd_hello_logic = formData.comment_2nd_hello_logic ? 1 : 0
            }
            if (fieldSelectors.cmnt_40sec_call_limit_check) {
              updateData.cmnt_40sec_call_limit_check = formData.cmnt_40sec_call_limit_check ? 1 : 0
            }
            if (fieldSelectors.dispo_in_cb_after_40sec) {
              updateData.dispo_in_cb_after_40sec = formData.dispo_in_cb_after_40sec ? 1 : 0
            }
            if (fieldSelectors.debug_mode) {
              updateData.debug_mode = formData.debug_mode ? 1 : 0
            }
            if (fieldSelectors.number_of_audios) {
              updateData.number_of_audios = parseInt(formData.number_of_audios) || 1
            }

            // Add user info for change tracking
            updateData._user_id = currentUser?.id || null
            updateData._user_name = currentUser?.username || null

            await licenseApi.update(license.id, updateData)
          }
          setSelectedLicenses([])
        } else {
          // Single edit
          const updateData = {
            id: currentLicense.id,
            user: users[0] || currentLicense.user,
            password: passwords[0] || currentLicense.password,
            access_link: formData.access_link,
            dialer: dialerName,
            dialer_id: parseInt(formData.dialer_id),
            campaign: formData.campaign,
            avatar_id: parseInt(formData.avatar_id),
            // Bot configuration
            login_method: formData.login_method || 'double-merged',
            transfer_method: formData.transfer_method || 'd1',
            xfer_disposition_selector: formData.xfer_disposition_selector || 'call-transfered',
            transcriber_type: formData.transcriber_type || 'VOSK',
            dont_wait: formData.dont_wait ? 1 : 0,
            comment_login_submit_btn: formData.comment_login_submit_btn ? 1 : 0,
            comment_2nd_hello_logic: formData.comment_2nd_hello_logic ? 1 : 0,
            cmnt_40sec_call_limit_check: formData.cmnt_40sec_call_limit_check ? 1 : 0,
            dispo_in_cb_after_40sec: formData.dispo_in_cb_after_40sec ? 1 : 0,
            debug_mode: formData.debug_mode ? 1 : 0,
            number_of_audios: parseInt(formData.number_of_audios) || 1,
            // Keep existing key_string and token
            // Add user info for change tracking
            _user_id: currentUser?.id || null,
            _user_name: currentUser?.username || null
          }
          await licenseApi.update(currentLicense.id, updateData)
        }
      } else {
        // ADD NEW LICENSES - This is the critical part!
        console.log('═══════════════════════════════════════════════════════')
        console.log('[Licenses] CREATING NEW LICENSE(S)')
        console.log('═══════════════════════════════════════════════════════')
        console.log('[Licenses] Form Data:', {
          dialer_id: formData.dialer_id,
          dialer_id_type: typeof formData.dialer_id,
          access_link: formData.access_link,
          avatar_id: formData.avatar_id,
          avatar_id_type: typeof formData.avatar_id,
          campaign: formData.campaign,
          users_count: users.length,
          passwords_count: passwords.length
        })
        console.log('[Licenses] Dialer Name:', dialerName)

        for (let i = 0; i < users.length; i++) {
          // Generate token and key_string EXACTLY like Angular
          const token = generateToken()
          const keyString = generateKeyString(token)

          // Validate before creating
          const parsedDialerId = parseInt(formData.dialer_id)
          const parsedAvatarId = parseInt(formData.avatar_id)

          if (isNaN(parsedDialerId) || parsedDialerId <= 0) {
            console.error('[Licenses] ERROR: Invalid dialer_id:', formData.dialer_id)
            alert('Error: Please select a valid Dialer')
            return
          }
          if (isNaN(parsedAvatarId) || parsedAvatarId <= 0) {
            console.error('[Licenses] ERROR: Invalid avatar_id:', formData.avatar_id)
            alert('Error: Please select a valid Avatar')
            return
          }
          if (!formData.access_link) {
            console.error('[Licenses] ERROR: No access_link selected')
            alert('Error: Please select an Access Link')
            return
          }

          const newLicense = {
            user: users[i],
            password: passwords[i] || passwords[0] || '',
            access_link: formData.access_link,
            dialer: dialerName,
            dialer_id: parsedDialerId,
            campaign: formData.campaign || '',
            avatar_id: parsedAvatarId,
            // CRITICAL: token is RAW 32-char, key_string is ENCRYPTED Base64
            token: token,
            key_string: keyString,
            // Bot configuration
            login_method: formData.login_method || 'double-merged',
            transfer_method: formData.transfer_method || 'd1',
            xfer_disposition_selector: formData.xfer_disposition_selector || 'call-transfered',
            transcriber_type: formData.transcriber_type || 'VOSK',
            dont_wait: formData.dont_wait ? 1 : 0,
            comment_login_submit_btn: formData.comment_login_submit_btn ? 1 : 0,
            comment_2nd_hello_logic: formData.comment_2nd_hello_logic ? 1 : 0,
            cmnt_40sec_call_limit_check: formData.cmnt_40sec_call_limit_check ? 1 : 0,
            dispo_in_cb_after_40sec: formData.dispo_in_cb_after_40sec ? 1 : 0,
            debug_mode: formData.debug_mode ? 1 : 0,
            number_of_audios: parseInt(formData.number_of_audios) || 1,
            // Add user info for change tracking
            _user_id: currentUser?.id || null,
            _user_name: currentUser?.username || null
          }

          console.log('───────────────────────────────────────────────────────')
          console.log(`[Licenses] License #${i + 1} FULL DATA:`)
          console.log('  user:', newLicense.user)
          console.log('  password:', newLicense.password)
          console.log('  access_link:', newLicense.access_link)
          console.log('  dialer:', newLicense.dialer)
          console.log('  dialer_id:', newLicense.dialer_id, '(type:', typeof newLicense.dialer_id, ')')
          console.log('  avatar_id:', newLicense.avatar_id, '(type:', typeof newLicense.avatar_id, ')')
          console.log('  campaign:', newLicense.campaign)
          console.log('  token:', newLicense.token, '(length:', newLicense.token.length, ')')
          console.log('  key_string:', newLicense.key_string)
          console.log('───────────────────────────────────────────────────────')

          const result = await licenseApi.create(newLicense)
          console.log('[Licenses] API Response:', result.data)
        }
        console.log('═══════════════════════════════════════════════════════')
        console.log('[Licenses] License creation complete!')
        console.log('═══════════════════════════════════════════════════════')
      }

      closeModal()
      loadData()
    } catch (error) {
      console.error('Failed to save license:', error)
      alert('Error saving license: ' + error.message)
    } finally {
      setSaving(false)
    }
  }

  // Delete handlers (matches Angular deleteLicense/deleteSelected)
  const handleDeleteSingle = async () => {
    if (!currentLicense) return
    setSaving(true)
    try {
      const userName = currentUser?.username || ''
      await fetch(`${API_BASE_URL}/tables/licenses/delete?field=id&val=${currentLicense.id}&user_name=${encodeURIComponent(userName)}`, { method: 'DELETE' })
      closeModal()
      setShowDeleteModal(false)
      loadData()
    } catch (error) {
      console.error('Failed to delete:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedLicenses.length === 0) return
    setSaving(true)
    try {
      const userName = currentUser?.username || ''
      for (const id of selectedLicenses) {
        await fetch(`${API_BASE_URL}/tables/licenses/delete?field=id&val=${id}&user_name=${encodeURIComponent(userName)}`, { method: 'DELETE' })
      }
      setSelectedLicenses([])
      setShowDeleteModal(false)
      loadData()
    } catch (error) {
      console.error('Failed to delete:', error)
    } finally {
      setSaving(false)
    }
  }

  // Copy key to clipboard
  const copyKey = (keyString) => {
    if (!keyString) return
    navigator.clipboard.writeText(keyString)
  }

  const hasActiveFilters = searchQuery || idMin || idMax

  return (
    <motion.div
      className="licenses-page"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="breadcrumb" variants={itemVariants}>
        <Link to="/">Dashboard</Link>
        <ChevronRight size={16} />
        <span>Licenses</span>
      </motion.div>

      {/* Header */}
      <motion.div className="page-header" variants={itemVariants}>
        <div>
          <h1 className="page-title">
            <Key size={28} />
            Licenses
          </h1>
          <p className="page-subtitle">Manage bot license keys and configurations</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => loadData(true)}
            disabled={refreshing}
            title="Refresh"
          >
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </button>
          <button className="btn btn-primary" onClick={openAddModal}>
            <Plus size={16} /> Add License
          </button>
        </div>
      </motion.div>

      {/* Stats Blocks */}
      <motion.div className="stat-blocks" variants={itemVariants}>
        <div className="stat-block blue">
          <div className="stat-block-icon"><Key size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.total}</span>
            <span className="stat-block-label">Total Licenses</span>
          </div>
        </div>
        <div className="stat-block green">
          <div className="stat-block-icon"><CheckCircle size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.withKeys}</span>
            <span className="stat-block-label">With Keys</span>
          </div>
        </div>
        <div className="stat-block purple">
          <div className="stat-block-icon"><Database size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.uniqueDialers}</span>
            <span className="stat-block-label">Dialers Used</span>
          </div>
        </div>
        <div className="stat-block orange">
          <div className="stat-block-icon"><Users size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{stats.uniqueAvatars}</span>
            <span className="stat-block-label">Avatars Used</span>
          </div>
        </div>
        {filteredLicenses.length !== licenses.length && (
          <div className="stat-block cyan">
            <div className="stat-block-icon"><Filter size={20} /></div>
            <div className="stat-block-content">
              <span className="stat-block-value">{filteredLicenses.length}</span>
              <span className="stat-block-label">Filtered</span>
            </div>
          </div>
        )}
      </motion.div>

      {/* Toolbar */}
      <motion.div className="toolbar" variants={itemVariants}>
        <div className="toolbar-left">
          <div className="input-with-icon">
            <Search size={16} className="icon" />
            <input
              type="text"
              className="input"
              placeholder="Filter by dialer name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-btn" onClick={() => setSearchQuery('')}>
                <X size={14} />
              </button>
            )}
          </div>
          <input
            type="number"
            className="input input-sm"
            placeholder="ID min"
            value={idMin}
            onChange={(e) => setIdMin(e.target.value)}
            style={{ width: 90 }}
          />
          <input
            type="number"
            className="input input-sm"
            placeholder="ID max"
            value={idMax}
            onChange={(e) => setIdMax(e.target.value)}
            style={{ width: 90 }}
          />
          {hasActiveFilters && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
        <div className="toolbar-right">
          <select
            className="input input-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map(size => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Select All Filtered (matches Angular) */}
      {hasActiveFilters && (
        <motion.div className="select-all-row" variants={itemVariants}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={filterAll}
              onChange={(e) => handleSelectAllFiltered(e.target.checked)}
            />
            <span>Select all {filteredLicenses.length} filtered results</span>
          </label>
        </motion.div>
      )}

      {/* Selection Actions (matches Angular) */}
      <AnimatePresence>
        {selectedLicenses.length > 0 && (
          <motion.div
            className="selection-bar"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <span className="selection-count">{selectedLicenses.length} selected</span>
            <button className="btn btn-secondary btn-sm" onClick={openBulkEditModal}>
              <Edit size={14} /> Edit Selected
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 size={14} /> Delete Selected
            </button>
            <button className="btn btn-ghost btn-sm" onClick={clearSelection}>
              Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <motion.div className="table-container" variants={itemVariants}>
        {loading ? (
          <div className="loading-state">
            <Loader2 size={32} className="spin" />
            <p>Loading licenses...</p>
          </div>
        ) : filteredLicenses.length === 0 ? (
          <div className="empty-state">
            <Key size={48} />
            <h3>No licenses found</h3>
            <p>{hasActiveFilters ? 'Try adjusting your filters' : 'Add your first license to get started'}</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>ID</th>
                    <th>Dialer</th>
                    <th>Access Link</th>
                    <th>User</th>
                    <th>Password</th>
                    <th>Avatar (ID)</th>
                    <th>Campaign</th>
                    <th>Key</th>
                    <th>Modified</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLicenses.map(license => (
                    <tr
                      key={license.id}
                      className={selectedLicenses.includes(license.id) ? 'selected' : ''}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedLicenses.includes(license.id)}
                          onChange={() => handleSelect(license.id)}
                        />
                      </td>
                      <td className="text-muted">{license.id}</td>
                      <td>{license.dialer || dialerMap.get(license.dialer_id) || '-'}</td>
                      <td className="cell-truncate" title={license.access_link}>
                        {license.access_link || '-'}
                      </td>
                      <td>{license.user || '-'}</td>
                      <td>{license.password || '-'}</td>
                      <td>
                        {license.avatar_name || avatarMap.get(license.avatar_id) || '-'}
                        {license.avatar_id && <span className="text-muted"> ({license.avatar_id})</span>}
                      </td>
                      <td>{license.campaign || '-'}</td>
                      <td>
                        {license.key_string ? (
                          <div className="key-cell">
                            <code className="key-code">{license.key_string}</code>
                            <button
                              className="btn btn-icon btn-ghost btn-xs"
                              onClick={() => copyKey(license.key_string)}
                              title="Copy key"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {license.modified_by ? (
                          <span
                            className="license-modified-info"
                            title={`${license.last_action === 'created' ? 'Created' : 'Modified'} by ${license.modified_by} on ${new Date(license.modified_at).toLocaleString()}`}
                          >
                            <Clock size={10} />
                            <span className="modified-user">{license.modified_by}</span>
                            <span className="modified-date">{new Date(license.modified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => openEditModal(license)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredLicenses.length)} of {filteredLicenses.length}
              </div>
              <div className="pagination-controls">
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  First
                </button>
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="pagination-current">Page {currentPage} of {totalPages}</span>
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 className="modal-title">
                  {modalMode === 'add' && 'Add License'}
                  {modalMode === 'edit' && 'Edit License'}
                  {modalMode === 'bulk-edit' && `Edit ${selectedLicenses.length} Licenses`}
                </h2>
                <button className="modal-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body">
                {/* Alerts */}
                {usersAlert && <div className="alert alert-warning">{usersAlert}</div>}
                {passwordsAlert && <div className="alert alert-warning">{passwordsAlert}</div>}

                {/* Dialer */}
                <div className="input-group">
                  <div className="input-label-row">
                    {modalMode === 'bulk-edit' && (
                      <input
                        type="checkbox"
                        className="field-selector"
                        checked={fieldSelectors.dialer}
                        onChange={(e) => {
                          setFieldSelectors({ ...fieldSelectors, dialer: e.target.checked })
                          setHasChanged(true)
                        }}
                      />
                    )}
                    <label className="input-label">Dialer</label>
                  </div>
                  <select
                    className="input"
                    value={formData.dialer_id}
                    onChange={(e) => {
                      setFormData({ ...formData, dialer_id: e.target.value })
                      setHasChanged(true)
                    }}
                  >
                    {dialers.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                {/* Access Link */}
                <div className="input-group">
                  <div className="input-label-row">
                    {modalMode === 'bulk-edit' && (
                      <input
                        type="checkbox"
                        className="field-selector"
                        checked={fieldSelectors.access_link}
                        onChange={(e) => {
                          setFieldSelectors({ ...fieldSelectors, access_link: e.target.checked })
                          setHasChanged(true)
                        }}
                      />
                    )}
                    <label className="input-label">Access Link</label>
                  </div>
                  <select
                    className="input"
                    value={formData.access_link}
                    onChange={(e) => {
                      setFormData({ ...formData, access_link: e.target.value })
                      setHasChanged(true)
                    }}
                  >
                    <option value="">Select access link...</option>
                    {accessLinks.map((link, idx) => (
                      <option key={idx} value={link}>{link}</option>
                    ))}
                  </select>
                </div>

                {/* Users */}
                <div className="input-group">
                  <div className="input-label-row">
                    {modalMode === 'bulk-edit' && (
                      <input
                        type="checkbox"
                        className="field-selector"
                        checked={fieldSelectors.user}
                        onChange={(e) => {
                          setFieldSelectors({ ...fieldSelectors, user: e.target.checked })
                          setHasChanged(true)
                        }}
                      />
                    )}
                    <label className="input-label">User</label>
                  </div>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Users comma separated (e.g., user1, user2, ...)"
                    value={formData.users}
                    onChange={(e) => {
                      setFormData({ ...formData, users: e.target.value })
                      validateUsers(e.target.value)
                      setHasChanged(true)
                    }}
                  />
                </div>

                {/* Passwords */}
                <div className="input-group">
                  <div className="input-label-row">
                    {modalMode === 'bulk-edit' && (
                      <input
                        type="checkbox"
                        className="field-selector"
                        checked={fieldSelectors.password}
                        onChange={(e) => {
                          setFieldSelectors({ ...fieldSelectors, password: e.target.checked })
                          setHasChanged(true)
                        }}
                      />
                    )}
                    <label className="input-label">Password</label>
                  </div>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Passwords comma separated (e.g., pass1, pass2, ...)"
                    value={formData.passwords}
                    onChange={(e) => {
                      setFormData({ ...formData, passwords: e.target.value })
                      validatePasswords(e.target.value)
                      setHasChanged(true)
                    }}
                  />
                </div>

                {/* Avatar */}
                <div className="input-group">
                  <div className="input-label-row">
                    {modalMode === 'bulk-edit' && (
                      <input
                        type="checkbox"
                        className="field-selector"
                        checked={fieldSelectors.avatar}
                        onChange={(e) => {
                          setFieldSelectors({ ...fieldSelectors, avatar: e.target.checked })
                          setHasChanged(true)
                        }}
                      />
                    )}
                    <label className="input-label">Avatar</label>
                  </div>
                  <select
                    className="input"
                    value={formData.avatar_id}
                    onChange={(e) => {
                      setFormData({ ...formData, avatar_id: e.target.value })
                      setHasChanged(true)
                    }}
                  >
                    {avatars.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>

                {/* Campaign */}
                <div className="input-group">
                  <div className="input-label-row">
                    {modalMode === 'bulk-edit' && (
                      <input
                        type="checkbox"
                        className="field-selector"
                        checked={fieldSelectors.campaign}
                        onChange={(e) => {
                          setFieldSelectors({ ...fieldSelectors, campaign: e.target.checked })
                          setHasChanged(true)
                        }}
                      />
                    )}
                    <label className="input-label">Campaign</label>
                  </div>
                  <input
                    type="text"
                    className="input"
                    placeholder="Campaign name"
                    value={formData.campaign}
                    onChange={(e) => {
                      setFormData({ ...formData, campaign: e.target.value })
                      setHasChanged(true)
                    }}
                  />
                </div>

                {/* Advanced Options Toggle */}
                <div className="advanced-toggle" style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="btn btn-ghost"
                    style={{ width: '100%', justifyContent: 'center', gap: '8px' }}
                  >
                    <Settings size={16} />
                    {showAdvanced ? 'Hide Bot Configuration' : 'Show Bot Configuration'}
                  </button>
                </div>

                {/* Advanced Bot Configuration */}
                {showAdvanced && (
                  <div className="bot-config-section" style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {/* Login Method */}
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <div className="input-label-row">
                          {modalMode === 'bulk-edit' && (
                            <input
                              type="checkbox"
                              className="field-selector"
                              checked={fieldSelectors.login_method}
                              onChange={(e) => {
                                setFieldSelectors({ ...fieldSelectors, login_method: e.target.checked })
                                setHasChanged(true)
                              }}
                            />
                          )}
                          <label className="input-label" style={{ fontSize: '0.75rem' }}>Login Method</label>
                        </div>
                        <select
                          className="input"
                          value={formData.login_method}
                          onChange={(e) => {
                            setFormData({ ...formData, login_method: e.target.value })
                            setHasChanged(true)
                          }}
                        >
                          <option value="single-v1">Single V1</option>
                          <option value="single-v2">Single V2</option>
                          <option value="repeating">Repeating</option>
                          <option value="double">Double</option>
                          <option value="double-merged">Double Merged</option>
                          <option value="second-try">Second Try</option>
                          <option value="at-law">At Law</option>
                          <option value="double-ziscom">Double Ziscom</option>
                        </select>
                      </div>

                      {/* Transfer Method */}
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <div className="input-label-row">
                          {modalMode === 'bulk-edit' && (
                            <input
                              type="checkbox"
                              className="field-selector"
                              checked={fieldSelectors.transfer_method}
                              onChange={(e) => {
                                setFieldSelectors({ ...fieldSelectors, transfer_method: e.target.checked })
                                setHasChanged(true)
                              }}
                            />
                          )}
                          <label className="input-label" style={{ fontSize: '0.75rem' }}>Transfer Method</label>
                        </div>
                        <select
                          className="input"
                          value={formData.transfer_method}
                          onChange={(e) => {
                            setFormData({ ...formData, transfer_method: e.target.value })
                            setHasChanged(true)
                          }}
                        >
                          <option value="dynamic">Dynamic</option>
                          <option value="inboundlocal">Inbound Local</option>
                          <option value="d1">D1</option>
                          <option value="d2">D2</option>
                          <option value="d3">D3</option>
                          <option value="d4">D4</option>
                          <option value="d5">D5</option>
                          <option value="d2-preset">D2 Preset</option>
                          <option value="Quick">Quick</option>
                          <option value="local">Local</option>
                          <option value="local-group">Local Group</option>
                          <option value="preset-dial-with-customer">Preset Dial With Customer</option>
                          <option value="dial-with-customer">Dial With Customer</option>
                          <option value="d1-preset">D1 Preset</option>
                          <option value="d4-preset">D4 Preset</option>
                          <option value="preset-voice">Preset Voice</option>
                          <option value="d1-cons-dwc">D1 Cons DWC</option>
                          <option value="d1-dial-with-customer">D1 Dial With Customer</option>
                          <option value="d2-dial-with-customer">D2 Dial With Customer</option>
                          <option value="localcloserconsultative">Local Closer Consultative</option>
                        </select>
                      </div>

                      {/* Transfer Disposition */}
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <div className="input-label-row">
                          {modalMode === 'bulk-edit' && (
                            <input
                              type="checkbox"
                              className="field-selector"
                              checked={fieldSelectors.xfer_disposition_selector}
                              onChange={(e) => {
                                setFieldSelectors({ ...fieldSelectors, xfer_disposition_selector: e.target.checked })
                                setHasChanged(true)
                              }}
                            />
                          )}
                          <label className="input-label" style={{ fontSize: '0.75rem' }}>Transfer Disposition</label>
                        </div>
                        <select
                          className="input"
                          value={formData.xfer_disposition_selector}
                          onChange={(e) => {
                            setFormData({ ...formData, xfer_disposition_selector: e.target.value })
                            setHasChanged(true)
                          }}
                        >
                          <option value="call-transfered">Call Transferred</option>
                          <option value="sale-made">Sale Made</option>
                        </select>
                      </div>

                      {/* Transcriber */}
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <div className="input-label-row">
                          {modalMode === 'bulk-edit' && (
                            <input
                              type="checkbox"
                              className="field-selector"
                              checked={fieldSelectors.transcriber_type}
                              onChange={(e) => {
                                setFieldSelectors({ ...fieldSelectors, transcriber_type: e.target.checked })
                                setHasChanged(true)
                              }}
                            />
                          )}
                          <label className="input-label" style={{ fontSize: '0.75rem' }}>Transcriber</label>
                        </div>
                        <select
                          className="input"
                          value={formData.transcriber_type}
                          onChange={(e) => {
                            setFormData({ ...formData, transcriber_type: e.target.value })
                            setHasChanged(true)
                          }}
                        >
                          <option value="VOSK">VOSK (Small)</option>
                          <option value="VOSK_V15L">VOSK (V15 Large)</option>
                          <option value="HYBRID">HYBRID (VOSK + Whisper)</option>
                          <option value="WHISPER_CPU">Whisper CPU</option>
                          <option value="WHISPER_S1">Whisper S1</option>
                          <option value="WHISPER_S2">Whisper S2</option>
                          <option value="WHISPER_S3">Whisper S3</option>
                          <option value="WHISPER_S4">Whisper S4</option>
                        </select>
                      </div>

                      {/* Number of Audios */}
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <div className="input-label-row">
                          {modalMode === 'bulk-edit' && (
                            <input
                              type="checkbox"
                              className="field-selector"
                              checked={fieldSelectors.number_of_audios}
                              onChange={(e) => {
                                setFieldSelectors({ ...fieldSelectors, number_of_audios: e.target.checked })
                                setHasChanged(true)
                              }}
                            />
                          )}
                          <label className="input-label" style={{ fontSize: '0.75rem' }}>Number of Audios</label>
                        </div>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          className="input"
                          value={formData.number_of_audios}
                          onChange={(e) => {
                            setFormData({ ...formData, number_of_audios: parseInt(e.target.value) || 1 })
                            setHasChanged(true)
                          }}
                        />
                      </div>
                    </div>

                    {/* Checkbox Options */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '8px',
                      marginTop: '12px',
                      fontSize: '0.8rem'
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        {modalMode === 'bulk-edit' && (
                          <input
                            type="checkbox"
                            className="field-selector"
                            checked={fieldSelectors.dont_wait}
                            onChange={(e) => {
                              setFieldSelectors({ ...fieldSelectors, dont_wait: e.target.checked })
                              setHasChanged(true)
                            }}
                            style={{ marginRight: '4px' }}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={formData.dont_wait}
                          onChange={(e) => {
                            setFormData({ ...formData, dont_wait: e.target.checked })
                            setHasChanged(true)
                          }}
                        />
                        <span>Don't Wait</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        {modalMode === 'bulk-edit' && (
                          <input
                            type="checkbox"
                            className="field-selector"
                            checked={fieldSelectors.comment_login_submit_btn}
                            onChange={(e) => {
                              setFieldSelectors({ ...fieldSelectors, comment_login_submit_btn: e.target.checked })
                              setHasChanged(true)
                            }}
                            style={{ marginRight: '4px' }}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={formData.comment_login_submit_btn}
                          onChange={(e) => {
                            setFormData({ ...formData, comment_login_submit_btn: e.target.checked })
                            setHasChanged(true)
                          }}
                        />
                        <span>Skip Login Btn</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        {modalMode === 'bulk-edit' && (
                          <input
                            type="checkbox"
                            className="field-selector"
                            checked={fieldSelectors.comment_2nd_hello_logic}
                            onChange={(e) => {
                              setFieldSelectors({ ...fieldSelectors, comment_2nd_hello_logic: e.target.checked })
                              setHasChanged(true)
                            }}
                            style={{ marginRight: '4px' }}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={formData.comment_2nd_hello_logic}
                          onChange={(e) => {
                            setFormData({ ...formData, comment_2nd_hello_logic: e.target.checked })
                            setHasChanged(true)
                          }}
                        />
                        <span>Skip 2nd Hello</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        {modalMode === 'bulk-edit' && (
                          <input
                            type="checkbox"
                            className="field-selector"
                            checked={fieldSelectors.cmnt_40sec_call_limit_check}
                            onChange={(e) => {
                              setFieldSelectors({ ...fieldSelectors, cmnt_40sec_call_limit_check: e.target.checked })
                              setHasChanged(true)
                            }}
                            style={{ marginRight: '4px' }}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={formData.cmnt_40sec_call_limit_check}
                          onChange={(e) => {
                            setFormData({ ...formData, cmnt_40sec_call_limit_check: e.target.checked })
                            setHasChanged(true)
                          }}
                        />
                        <span>Skip 40s Limit</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        {modalMode === 'bulk-edit' && (
                          <input
                            type="checkbox"
                            className="field-selector"
                            checked={fieldSelectors.dispo_in_cb_after_40sec}
                            onChange={(e) => {
                              setFieldSelectors({ ...fieldSelectors, dispo_in_cb_after_40sec: e.target.checked })
                              setHasChanged(true)
                            }}
                            style={{ marginRight: '4px' }}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={formData.dispo_in_cb_after_40sec}
                          onChange={(e) => {
                            setFormData({ ...formData, dispo_in_cb_after_40sec: e.target.checked })
                            setHasChanged(true)
                          }}
                        />
                        <span>CB After 40s</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        {modalMode === 'bulk-edit' && (
                          <input
                            type="checkbox"
                            className="field-selector"
                            checked={fieldSelectors.debug_mode}
                            onChange={(e) => {
                              setFieldSelectors({ ...fieldSelectors, debug_mode: e.target.checked })
                              setHasChanged(true)
                            }}
                            style={{ marginRight: '4px' }}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={formData.debug_mode}
                          onChange={(e) => {
                            setFormData({ ...formData, debug_mode: e.target.checked })
                            setHasChanged(true)
                          }}
                        />
                        <span>Debug Mode</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                {modalMode === 'edit' && currentLicense && selectedLicenses.length === 0 && (
                  <button
                    className="btn btn-ghost btn-danger"
                    onClick={() => {
                      setShowModal(false)
                      setShowDeleteModal(true)
                    }}
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                )}
                <div className="modal-footer-right">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  {hasChanged && (
                    <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={saving || usersAlert || passwordsAlert}
                    >
                      {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                      Save
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              className="modal modal-sm"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 className="modal-title">
                  <AlertTriangle size={20} className="text-danger" />
                  {selectedLicenses.length > 0 ? 'Delete Selected' : 'Delete License'}
                </h2>
                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <p>
                  {selectedLicenses.length > 0
                    ? `Are you sure you want to delete ${selectedLicenses.length} licenses?`
                    : `Are you sure you want to delete license ID ${currentLicense?.id}?`
                  }
                </p>
                <p className="text-muted">This action cannot be undone.</p>
                {selectedLicenses.length > 10 && saving && (
                  <p className="text-muted">Please wait, deleting licenses...</p>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={selectedLicenses.length > 0 ? handleDeleteSelected : handleDeleteSingle}
                  disabled={saving}
                >
                  {saving ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default Licenses